import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../../assets/Icons";
import SmallPopupWithInput from "../../../../../components/_utils/CommonSmallPopupWithInput";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../../components/_utils/CommonToast";
import { formatAccountNumber } from "../../../../../components/_utils/FormatAccountNumber";
import UniversalRefreshToast from "../../../../../components/_utils/UniversalRefreshToast";
import {
  approveBeneficiaryAccount,
  beneficiaryAccountDetailsById,
  rejectBeneficiaryAccount,
} from "../../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import {
  bankLookUp,
  panLookUp,
} from "../../../../../redux/features/sharedResources/sharedResources";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";

const BeneficiaryAccountEditPANSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updatedFields = location?.state?.updatedFields || [];
  const [isFocused, setIsFocused] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    categoryIds: [],
    pan: "",
    labels: "",
  });

  const services = useSelector((state) => state.refreshToken.services);
  const { beneficiaryAccountDetailsData } = useSelector(
    (state) => state.beneficiaryAccount,
  );
  const { panLookupData, bankLookupData } = useSelector(
    (state) => state.sharedResources,
  );

  const beneAccountSuccessData =
    beneficiaryAccountDetailsData?.beneficiary_accounts[0];

  const canApprove =
    beneAccountSuccessData?.approval_status === "pending" &&
    services?.includes("BAAccount");

  const canCreateTxn = services?.includes("CBeneficiaryTxn");

  const handleInputChange = (event) => {
    let value = event?.target?.value;
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(0, 150);
    setDeclineReason(value);
  };

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }

    return focused ? "typing" : "error";
  };

  const fieldsToShow =
    updatedFields.length > GlobalConfiguration.lengthCheckZero
      ? updatedFields
      : ["pan_no", "gstin_detail"];

  const panGstFieldConfig = {
    pan_no: {
      leftSubTitle: "PAN",
      getTitle: (acc) => acc?.pan_no,
    },
    gstin_detail: {
      leftSubTitle: location?.state?.isGstnPresent
        ? "GSTIN"
        : "GSTIN (optional)",
      getTitle: (acc) => acc?.gstin_detail,
    },
  };

  const onlyGstUpdated =
    updatedFields.length === GlobalConfiguration.lengthCheckOne &&
    updatedFields[0] === "gstin_detail";
  const onlyPanUpdated =
    updatedFields.length === GlobalConfiguration.lengthCheckOne &&
    updatedFields[0] === "pan_no";
  const getPanGstSuccessTitle = () => {
    if (onlyGstUpdated) return "Account details updated successfully.";
    if (onlyPanUpdated) return "Account details updated successfully.";
    return "Account details updated successfully.";
  };

  const renderPanGstCards = () => {
    return fieldsToShow
      .filter((key) => panGstFieldConfig[key])
      .filter(
        (key) => key !== "gstin_detail" || beneAccountSuccessData?.gstin_detail,
      )
      .map((key) => {
        const config = panGstFieldConfig[key];
        const title = config.getTitle(beneAccountSuccessData);
        if (key === "gstin_detail" && !title) return null;
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: title || "Not given",
                leftSubTitle: config.leftSubTitle,
              },
            ]}
          />
        );
      });
  };

  const handlers = {
    createAccount: () =>
      navigate("/beneficiary_account_dashboard/create_beneficiary_account", {
        replace: true,
      }),
    createTxn: () => {
      if (!services?.includes("CBeneficiaryTxn")) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "CBeneficiaryTxn" },
        });
        return;
      }

      navigate(
        "/beneficiary_transaction_dashboard/create_beneficiary_transaction",
        {
          state: currentAccountDetails,
          replace: true,
        },
      );
    },
    viewDetails: () =>
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () =>
      navigate("/beneficiary_account_dashboard", {
        replace: true,
      }),
  };

  const accountApproval = () => {
    (async () => {
      setShowLoader();
      toast.dismiss();
      UniversalRefreshToast("approvalaccountsRefresh");
      let response = await dispatch(approveBeneficiaryAccount({ id }));
      if (approveBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: "Beneficiary account approved.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
      }
    })();
  };

  const accountRejected = () => {
    (async () => {
      setShowLoader();
      toast.dismiss();
      UniversalRefreshToast("accountsRefresh");
      let response = await dispatch(
        rejectBeneficiaryAccount({
          id,
          rejection_reason: declineReason,
        }),
      );
      if (rejectBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();
        CommonToast({
          text: "Beneficiary account rejected.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });

        setRejectPopup(false);
      }
    })();
  };

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
    hoverOption: "dark",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
    hoverOption: "dark",
  };

  const buttonRows = [
    ...(canApprove
      ? [
          [
            {
              btnText: globalText?.REJECT_ACCOUNT,
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                setRejectPopup(true);
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
            {
              btnText: globalText?.APPROVE_ACCOUNT,
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                accountApproval();
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
            },
          ],
        ]
      : []),

    [
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_ACCOUNT,
        icon: ICONS?.CREATE_NEW_ACCOUNT,
        handleFunction: handlers.createAccount,
      },
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_TRANSACTION,
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: handlers.createTxn,
        isDisabled: !canCreateTxn,
      },
      {
        ...primaryBtn,
        btnText: globalText?.VIEW_DETAILS,
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: handlers.viewDetails,
      },
      {
        ...outlineBtn,
        btnText: globalText?.GO_TO_DASHBOARD,
        icon: ICONS?.GO_TO_DASHBOARD,
        handleFunction: handlers.dashboard,
        imgColor: "var(--text-primary)",
      },
    ],
  ];

  useEffect(() => {
    dispatch(beneficiaryAccountDetailsById({ id }));
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (!beneAccountSuccessData) return;

    dispatch(panLookUp({ pan_number: beneAccountSuccessData.pan_no }));
    dispatch(
      bankLookUp({
        ifsc: beneAccountSuccessData.ifsc_code,
        bank_account_no: beneAccountSuccessData.bank_acc_no,
      }),
    );
  }, [beneAccountSuccessData, dispatch]);

  useEffect(() => {
    if (beneAccountSuccessData) {
      const {
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category_ids,
        pan_no,
        labels,
      } = beneAccountSuccessData;
      const categoryIds = (category_ids || []).map(
        (c) => c.category_id ?? c.id,
      );
      setCurrentAccountDetails({
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        categoryIds,
        pan: pan_no,
        labels: labels,
      });
    }
  }, [beneAccountSuccessData]);

  return (
    <>
      {rejectPopup && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-max-zindex bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SmallPopupWithInput
            headerTitle={globalText?.REJECT_REASON_LABEL}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            secondHeaderTitle={globalText?.REJECT_POPUP_SUB_TEXT}
            secondHeaderFontWeight="normal"
            secondHeaderFontColor="text-secondary"
            secondHeaderTextSize="font-xs"
            noteText={globalText?.REJECT_POPUP_ACCOUNT_NOTE_TEXT}
            noteColor="text-secondary"
            bulletPoints={[
              globalText.REJECT_POPUP_BULLET_POINT_ONE,
              globalText.REJECT_POPUP_BULLET_POINT_TWO,
              globalText.REJECT_POPUP_BULLET_POINT_THREE,
            ]}
            inputPlaceholder={globalText.REASON}
            inputVariant={computeVariant(declineReason, isFocused)}
            inputOnFocus={(e) => setIsFocused(true)}
            inputOnBlur={(e) => setIsFocused(false)}
            btnOneIsDisabled={showLoader ? true : false}
            inputBgColor="var(--primary-bg)"
            inputFgColor="var(--text-secondary)"
            inputOnInput={(e) => {
              if (!showLoader) {
                handleInputChange(e);
              }
            }}
            inputValue={declineReason}
            inputFontKind="big"
            inputIsFullWidth={true}
            btnOneText="Cancel"
            btnOneHandleFunction={() => {
              setRejectPopup(false);
              setDeclineReason("");
            }}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneHoverOption="white"
            btnTwoText="Reject"
            btnTwoHandleFunction={() => {
              accountRejected();
              setDeclineReason("");
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={
              declineReason.trim().length <
              GlobalConfiguration.rejectReasonMinLength
            }
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
            btnTwoInActiveVariantNeeded={true}
          />
        </div>
      )}
      <div>
        <StatusContainer
          status="success"
          buttonRows={buttonRows}
          title={getPanGstSuccessTitle()}
        >
          <TabsWeapperSection title="Account details">
            <>
              {renderPanGstCards()}
              <InfoCard
                bgColor="var(--positive-card)"
                rows={[
                  {
                    leftTitle: beneAccountSuccessData?.id,
                    leftSubTitle: "Account ID",
                    copyText: beneAccountSuccessData?.id,
                  },
                ]}
              />
            </>
          </TabsWeapperSection>
          {panLookupData?.beneficiary_accounts?.length >
            GlobalConfiguration.lengthCheckOne && (
            <TabsWeapperSection title="Accounts associated to PAN">
              {panLookupData?.beneficiary_accounts
                ?.filter(
                  (accountData) =>
                    accountData?.approval_status === "pending" ||
                    accountData?.approval_status === "approved",
                )
                .map((accountData, id) => (
                  <InfoCard
                    key={id}
                    bgColor="var(--positive-card)"
                    showRowDivider={true}
                    rowBorderColor="var(--positive-bg)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderWidth="w-width-lg"

                    rows={[
                      {
                        leftTitle: accountData?.bank_account_holder_full_name,
                        leftSubTitle: `${
                          accountData?.approval_status
                            ?.charAt(0)
                            .toUpperCase() +
                          accountData?.approval_status?.slice(1)
                        } account`,
                      },
                      {
                        leftTitle: formatAccountNumber(
                          accountData?.bank_acc_no,
                        ),
                        leftSubTitle: "Account number",
                        ifsc: accountData?.ifsc_code,
                      },
                    ]}
                  />
                ))}
            </TabsWeapperSection>
          )}
          {bankLookupData?.beneficiary_accounts?.length >
            GlobalConfiguration.lengthCheckOne && (
            <TabsWeapperSection title="Accounts associated to bank">
              {bankLookupData?.beneficiary_accounts
                .filter(
                  (accountData) =>
                    accountData?.approval_status === "pending" ||
                    accountData?.approval_status === "approved",
                )
                .map((accountData, id) => (
                  <InfoCard
                    key={id}
                    bgColor="var(--positive-card)"
                     showRowDivider={true}
                    rowBorderColor="var(--positive-bg)"
                    borderColor="var(--positive-bg)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderWidth="w-width-lg"
                    rows={[
                      {
                        leftTitle: accountData?.name,
                        leftSubTitle: `${
                      accountData?.approval_status?.charAt(0).toUpperCase() +
                      accountData?.approval_status?.slice(1)
                    } account`,
                      },
                      {
                        leftTitle: accountData?.pan_no,
                        leftSubTitle: "PAN",
                      },
                    ]}
                  />
                ))}
            </TabsWeapperSection>
          )}
        </StatusContainer>
      </div>
    </>
  );
};

export default BeneficiaryAccountEditPANSuccess;
